import filterActiveSteps from "./filterActiveSteps";
import evaluateOutputCalculation from "./evaluateOutputCalculation";
import convertRecursiveRows from "./convertRecursiveRows";
import _ from "lodash";
import {getCalculationProperty} from "./getCalculationProperty";
import retrieveKCCOptimizationResponses from "./retrieveKCCOptimizationResponses";
import getKccResultContextKey from "./getKccResultContextKey";
import {getActive} from "./getActive";
import retrieveKColorResponses from "./retrieveKColorResponses";
import getKColorResultContextKey from "./getKColorResultContextKey";
/**
 * Updated to fix a bug; Hubspot ticket 2800382902
 * The previous version of the function was not considering the
 * case where the calculation had more than one product
 */
export const getProductIds = (calculation, responses) => {
  let res = []

  calculation.forEach(v => {
    if (v[0] === "{") {
      const {outputType, product, column, inputId} = JSON.parse(v);
      if (column === 'prezzo') {
        if (outputType === 'product') {
          res.push(product.value)
        } else {
          res.push(responses[inputId]?.option.product_id)
        }
      }
    }
  })
  return res
}

const checkIfOutputIsProductKpi = (output, responses) => {
  if (output.output_type === 'calculation') {
    /*
      hubspot ticket 21845089189
      filtering out undefined or null values
     */
    return getProductIds(output.calculation, responses).filter(p => p)
  } else {
    return []
  }
}

const checkIfOutputIsKColorProductKpi = (output, context) => {

  const res = []
  output.calculation.forEach(v => {
    if (v[0] === "{") {
      const {outputId, field} = JSON.parse(v);
      const nestedOutput = context.configuration.find(b => b.id === outputId)
      if (
        nestedOutput?.output_type === 'calculation'
        && nestedOutput.calculation.length === 1
        && nestedOutput.calculation[0][0] === "{"
      ) {
        const {original} = JSON.parse(nestedOutput.calculation[0]);
        if (original === 'kcolor' && field === 'price')
          res.push(nestedOutput)
      }
    }
  })

  return res
}

const checkIfOutputIsTotalKpi = (output) => {
  const name = output.name.toLowerCase()
  return output.output_type === 'calculation' && name.includes('configuration_total')
}

const generateProductFromKccResultRow = (row) => ({
  art_cod: row.product_code,
  art_des_it: row.entry_description,
  art_des_cmpy: row.entry_description,
  resa: null,
  base_um: row.entry_mu,
  min_qty: row.entry_qty_per_package,
  prezzo: row.entry_unit_price,
  flg_kpi: true,
});

export const getConfigurationKpi = async (apiRequest, context, getOptionTitle, surveyName=null,) => {

  /*
  HUBSPOT TICKET 3461473720
  there was a kcc product missing in "distinta materiali" and in quotation because
  the kcc products where taken directly from db cache and not from outputs as in web or pdf
  we need to compute them from the outputs!!!
  kccOptimizationResults needs to be overridden as in permanentSurvey.js in order to make every
  output access correctly the kcc product cached results
  also hubspot ticket 3592100110, kccOptimizationResults may not be an array in certaing configurations and it gives an error
  when saving
   */
  const correctKccOptimizationResults = Array.isArray(context.kccOptimizationResults)? context.kccOptimizationResults.reduce(
    (prev, curr) => {
      const kccResultContextKey = getKccResultContextKey({
        color: curr.cod_color,
        system: curr.cod_system,
        surface: curr.val_surface,
        survey: curr.cod_survey,
        primers: curr.lst_primers,
        reti: curr.lst_reti,
        rasanti: curr.lst_rasanti,
        is_tintometer: curr.is_tintometrist_version
      });
      return {...prev, [kccResultContextKey]: curr};
    },
    {}
  ): context.kccOptimizationResults;

  let kpis = []
  let productsKpi = {}
  let kColorProductsKpi = {}
  let totalKpi = undefined

  // The following object is going to be used to store the quantity of each product inside the recursive questions (if
  // any). Its structure is going to be the following:
  //
  //    {
  //      product_id_1: {
  //        recursive_question_id_1: [quantity_1, quantity_2, ...],
  //        recursive_question_id_2: [quantity_1, quantity_2, ...],
  //        ...
  //      },
  //      product_id_2: {
  //        recursive_question_id_1: [quantity_1, quantity_2, ...],
  //        ...
  //      },
  //      ...
  //    }
  //
  const recursiveQuestionsSplitQuantities = {};
  /*
  HUBSPOT TICKET 3461473720
  there was a kcc product missing in "distinta materiali" and in quotation because
  the kcc products where taken directly from db cache and not from outputs as in web or pdf
  we need to compute them from the outputs!!!
   */
  const addKccProductToKpis = (output, responses, context) => {
    const kccResponses = retrieveKCCOptimizationResponses(output, responses, context);
    const kccKey = getKccResultContextKey(kccResponses)
    const data = correctKccOptimizationResults[kccKey]
    data?.value.forEach((row) => {
      const {
        product_id,
        entry_total_price: value,
        entry_qty: quantity
      } = row;
      const info = !!product_id
        ? null
        : {
          product: generateProductFromKccResultRow(row),
          quantity
        };
      kpis.push({kind: 'kcc-product', product_id: product_id, value, quantity, info})
    });
  }

  const addKColorsProductToKpis = (output, context, responses, kColorOutput, recursiveQuestion) => {

    let value = evaluateOutputCalculation(output, {
      ...context,
      responses
    });
    const {product, color, productPackage} = retrieveKColorResponses(responses, kColorOutput);
    const key = getKColorResultContextKey(product, color, productPackage);

    const result = context?.kColorResults[key];
    const priceKey =
      context?.globalTargetPrice
      || context?.kColorPricesChosen?.[key]?.chosenPrice
      || "kerakoll";
    const price = [null, undefined, "kerakoll"].includes(priceKey) ?
      result?.price?.kerakoll :
      result?.price?.tintometer;

    const quantity = value / (price/result?.min_qty);
    const product_id = result?.product_id;

    if (kColorProductsKpi[key]) {
      kColorProductsKpi[key].value += value;
      kColorProductsKpi[key].quantity += quantity;
    } else {
      kColorProductsKpi[key] = {
        value,
        quantity,
        product_id,
        // used the retrieve it in the context and to guarantee unicity (is
        // the product art_cod + the kcolor code chosen)
        kcolor_key: key,
        product: {
          // this is used
          prezzo_confezione: [null, undefined].includes(price) ? result?.price?.kerakoll : price,
          prezzo: price / context.kColorResults[key]?.min_qty,
          art_cod: context.kColorResults[key]?.art_cod,
          product_des: context.kColorResults[key]?.product_des,
          code: context.kColorResults[key]?.code,
          min_qty: context.kColorResults[key]?.min_qty,
          base_um: context.kColorResults[key]?.um,
          link_scheda_tecnica: context.kColorResults[key]?.link_scheda_tecnica,
          link_image: context.kColorResults[key]?.link_image,
          id: context.kColorResults[key]?.product_id,
          resa: context.kColorResults[key]?.resa,
          unita_mis_resa: context.kColorResults[key]?.um_resa,
        },
        kcolor_color_code: context.kColorResults[key]?.kcolor_code,
        kcolor_color_rgb: context.kColorResults[key]?.kcolor_rgb,
      }
    }
    if (!!recursiveQuestion) {
      if (!(key in recursiveQuestionsSplitQuantities)) {
        recursiveQuestionsSplitQuantities[key] = {};
      }
      if (!(recursiveQuestion.id in recursiveQuestionsSplitQuantities[key])) {
        recursiveQuestionsSplitQuantities[key][recursiveQuestion.id] = [];
      }
      recursiveQuestionsSplitQuantities[key][recursiveQuestion.id].push(value);
    }

    // kpis.push({
    //   kind: "kcolor-product",
    //   info: {
    //     product: {
    //       prezzo: context.kColorResults[kColorOutputId].price.kerakoll
    //     },
    //     quantity: quantity,
    //     survey_id: context.survey.id,
    //     kcolor_color_code: context.kColorResults[kColorOutputId].kcolor_code,
    //     kcolor_color_rgb: context.kColorResults[kColorOutputId].kcolor_rgb,
    //   },
    //   value
    // });
  }

  filterActiveSteps(context).forEach(s => {

    // Skip inactive blocks
    if (!getActive(s, context)) return;

    s.blocks.forEach(i => {
      /*
      there may be inactive blocks in the configuration whose
      calculation contains products that should not be counted
      Hubspot ticket 2531139214 reported a case where H40 Extreme ended up in the KPIs
      and in the report but the block was inactive
      */
      if (!getActive(i, context)) return
      const productIds = checkIfOutputIsProductKpi(i, context.responses)
      if (productIds.length) {
        productIds.forEach(productId => {
          const pricesBackup = {}
          // make sure other products are zero!
          for (const otherProduct of productIds){
            if (otherProduct !== productId) {
              pricesBackup[otherProduct] = context.products[otherProduct].prezzo
              context.products[otherProduct].prezzo = 0
            }
          }
          productsKpi[productId] = (productsKpi[productId] || 0) + evaluateOutputCalculation(i, context)
          // make sure to reset the product price to the original value
          for (const otherProduct of productIds) {
            if (otherProduct !== productId) {
              context.products[otherProduct].prezzo = pricesBackup[otherProduct]
            }
          }
        })
      }

      if (checkIfOutputIsTotalKpi(i)) {
        totalKpi = evaluateOutputCalculation(i, context)
      }

      let kColorOutputs = checkIfOutputIsKColorProductKpi(i, context)

      if (kColorOutputs.length > 0) {
        if(kColorOutputs.length === 1) {
          addKColorsProductToKpis(i, context, context.responses, kColorOutputs[0]);
        } else {
          throw new Error("Non possono esserci più di un prodotto kcolor in un blocco")
        }
      }

      if (getCalculationProperty(i, "original") === "kcc_optimization") {
        addKccProductToKpis(i, context.responses, {
          ...context,
          kccOptimizationResults: correctKccOptimizationResults,
        });
      }

      if (i.type === "recursive_question") {
        const rows = convertRecursiveRows(
          context.responses
            ?.[i.id]
            ?.recursive_question_value
          || [],
          i.blocks,
          getOptionTitle,
          context,
          i.id,
        );
        rows.forEach((r, index) => {
          i.blocks.forEach(j => {
            /*
            there may be inactive blocks in the configuration whose
            calculation contains products that should not be counted
            same as Hubspot ticket 2531139214 but this time the block is
            inside a recursive question and the products referred are 
            GEOLITE GEL A and GEOLITE GEL B.
            */
            if (!getActive(j, context)) return
            /*
            hubspot ticket https://app.hubspot.com/contacts/8760420/record/0-5/21845089189/
            In this ticket in a recursive question Geolite Gel A and Geolite Gel B and Geosteel G3300
            were present but not added to the products kpi because they evaluated to zero.
            before: if r[j.id]
            after: if (r.hasOwnProperty(j.id))
            by doing as before we lost all products contained in calculations that evaluated to zero!
            and we don't want that.
             */
            if (r.hasOwnProperty(j.id) && r[j.id] !== '') {
              const rowResponses = {
                // all responses besides current recursive question
                ..._.omit(context.responses, [i.id]),
                // current recursive question iteration (index)
                ...context.responses[i.id].recursive_question_value[index],
              }
              const productIds = checkIfOutputIsProductKpi(j, rowResponses);
              if (productIds.length) {
                productIds.forEach(productId => {
                  const pricesBackup = {}
                  // make sure other products are zero!
                  for (const otherProduct of productIds) {
                    if (otherProduct !== productId) {
                      pricesBackup[otherProduct] = context.products[otherProduct].prezzo
                      context.products[otherProduct].prezzo = 0
                    }
                  }
                  const productTotal = evaluateOutputCalculation(j, {
                    ...context,
                    responses: rowResponses
                  }, undefined, index)

                  // make sure to reset the product price to the original value
                  for (const otherProduct of productIds) {
                    if (otherProduct !== productId) {
                      context.products[otherProduct].prezzo = pricesBackup[otherProduct]
                    }
                  }
                  // --- update the global quantity for the current product
                  productsKpi[productId] = (productsKpi[productId] || 0) + productTotal;
                  // --- update the quantities split by recursive question
                  if (!(productId in recursiveQuestionsSplitQuantities))
                    // first level: product key
                    recursiveQuestionsSplitQuantities[productId] = {};
                  if (!(i.id in recursiveQuestionsSplitQuantities[productId]))
                    // second level: recursive question key
                    recursiveQuestionsSplitQuantities[productId][i.id] = [];
                  // third level: array of quantities
                  recursiveQuestionsSplitQuantities[productId][i.id].push(productTotal);
                })
              }

              let kColorOutputsRecursive = checkIfOutputIsKColorProductKpi(j, context)
              if (kColorOutputsRecursive.length > 0) {
                if (kColorOutputsRecursive.length === 1) {
                  addKColorsProductToKpis(j, context, rowResponses, kColorOutputsRecursive[0],i);
                } else {
                  throw new Error("Non possono esserci più di un prodotto kcolor in un blocco")
                }
              }
            }
            // maybe it's a KCC ??
            if (j.id in r && getCalculationProperty(j, "original") === "kcc_optimization") {
              const rowResponses = {
                // all responses besides current recursive question
                ..._.omit(context.responses, [i.id]),
                // current recursive question iteration (index)
                ...context.responses[i.id].recursive_question_value[index],
              }
              addKccProductToKpis(j, rowResponses, {
                ...context,
                kccOptimizationResults: correctKccOptimizationResults
              })
            }
          })
        })
      }
    })
  })

  if (totalKpi) {
    kpis.push({kind: 'total', product_id: undefined, value: totalKpi})
  }

  const productsMissingInContext = Object.keys(productsKpi).filter(k => !context.products[k])
  if (productsMissingInContext.length > 0) {
    const result = await apiRequest.get(`/products/all?ids=${productsMissingInContext.join(",")}`)

    result.forEach(p => {
      context.products[p.id] = p
    })
  }

  Object.keys(productsKpi).forEach(k => {
    const rqs = recursiveQuestionsSplitQuantities[k] ?? {};
    const rqsTotal = Object.values(rqs)
      .reduce(
        (acc, rows) => acc + rows.reduce((a, b) => a + b, 0),
        0,
      );
    kpis.push({
      kind: 'product',
      product_id: k,
      value: productsKpi[k],
      quantity: productsKpi[k] / context?.products[k]?.prezzo,
      splitQuantities: {
        survey_name: surveyName,
        main: productsKpi[k] - rqsTotal,
        recursive_questions: rqs
      }
    })
  })

  Object.keys(kColorProductsKpi).forEach(k => {
    const rqs = recursiveQuestionsSplitQuantities[k] ?? {};
    const rqsTotal = Object.values(rqs)
      .reduce(
        (acc, rows) => acc + rows.reduce((a, b) => a + b, 0),
        0,
      );
    kpis.push({
      kind: 'kcolor-product',
      product_id: null,
      kcolor_cod_prod_comm: kColorProductsKpi[k].product.code,
      value: kColorProductsKpi[k].value,
      kcolor_key: kColorProductsKpi[k].kcolor_key,
      quantity: kColorProductsKpi[k].quantity,
      info: {
        product: kColorProductsKpi[k].product,
        quantity: kColorProductsKpi[k].quantity,
        survey_id: kColorProductsKpi[k].survey_id,
        kcolor_color_code: kColorProductsKpi[k].kcolor_color_code,
        kcolor_color_rgb: kColorProductsKpi[k].kcolor_color_rgb,
      },
      splitQuantities: {
        survey_name: surveyName,
        main: kColorProductsKpi[k].value - rqsTotal,
        recursive_questions: rqs
      }
    })
  });


  // Object.values(context.kccOptimizationResults ?? {}).forEach((kor) => {
  //   kor.value.forEach((row) => {
  //     const {
  //       product_id,
  //       entry_total_price: value,
  //       entry_qty: quantity
  //     } = row;
  //     const info = !!product_id
  //       ? null
  //       : {
  //         product: generateProductFromKccResultRow(row),
  //         quantity
  //       };
  //     kpis.push({kind: 'kcc-product', product_id: product_id, value, info})
  //   });
  // });

  return kpis;
}

export const storeConfigurationKpi = async (context, getOptionTitle, apiRequest) => {

  const kpis = await getConfigurationKpi(apiRequest, {
    ...context,
    // Ensure that globalTargetPrice is set to "kerakoll" when storing KPIs,
    globalTargetPrice: "kerakoll",
  }, getOptionTitle)
    .then((data) => {
      // Reduce KPIs in order to merge entries with same kind-product, to avoid duplicates
      const reducedKpis = data.reduce((acc, curr) => {
        const key = `${curr.kind}-${curr.product_id || "null"}`;
        const item = _.pick(curr, ["kind", "product_id", "value", "quantity","kcolor_cod_prod_comm"]);
        if (!(key in acc))
          acc[key] = {...item, value: 0, quantity: 0};
        acc[key].value += curr.value ?? 0;
        acc[key].quantity += curr.quantity ?? 0;
        return acc;
      }, {});
      return Object.values(reducedKpis);
    })

  return apiRequest.post(
    `/service_surveys_kpi/save_survey_kpi/${context.survey.id}`,
    kpis.map((i) => ({
      service_id: context.survey.service_id,
      survey_id: context.survey.id,
      worksite_id: context.survey.worksite_id,
      r_perm: context.survey.r_perm,
      w_perm: context.survey.w_perm,
      ...i,
    }))
  );
}